/**
 * Pane de lista — densidade enterprise estilo Linear/Attio.
 * Ícone-tile colorido por módulo + título + msg + timestamp + dot de unread.
 *
 * @version 3.0.0 - 2026-04-20 — refinamento "Enterprise Premium"
 */
import { Fragment, useMemo } from 'react';
import { Inbox, RefreshCw } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import {
  getNotificationIcon,
  getModulePalette,
  formatEditorialDate,
  getDateBucket,
  getDateBucketLabel,
  DATE_BUCKET_ORDER,
  type DateBucket,
} from './notification-utils';
import { NotificationBulkActionsBar } from './NotificationBulkActionsBar';
import type { NotificationCenterItem } from '@/hooks/useNotificationCenter';

interface Props {
  items: NotificationCenterItem[];
  loading: boolean;
  activeId: string | null;
  selectedIds: Set<string>;
  onSelectItem: (id: string) => void;
  onToggleSelected: (id: string) => void;
  onBulkMarkRead: () => void;
  onBulkMarkUnread: () => void;
  onBulkArchive: () => void;
  onBulkClear: () => void;
}

export function NotificationListPane({
  items, loading, activeId, selectedIds,
  onSelectItem, onToggleSelected,
  onBulkMarkRead, onBulkMarkUnread, onBulkArchive, onBulkClear,
}: Props) {
  // v3.1.0: calcula se há algum item NÃO lido dentro da seleção corrente
  // para o toggle dinâmico do botão de leitura na régua de ações em lote.
  const hasUnreadSelected = useMemo(
    () => items.some(i => selectedIds.has(i.id) && !i.is_read),
    [items, selectedIds],
  );


  const grouped = useMemo(() => {
    const map = new Map<DateBucket, NotificationCenterItem[]>();
    for (const it of items) {
      const b = getDateBucket(it.created_at);
      if (!map.has(b)) map.set(b, []);
      map.get(b)!.push(it);
    }
    return DATE_BUCKET_ORDER
      .filter(b => map.has(b))
      .map(b => ({ bucket: b, items: map.get(b)! }));
  }, [items]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <RefreshCw className="h-4 w-4 animate-spin mr-2" strokeWidth={1.75} />
        <span className="text-[13px]">Carregando…</span>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-20 text-muted-foreground px-6 text-center">
        <div className="h-14 w-14 rounded-full bg-muted flex items-center justify-center mb-5">
          <Inbox className="h-6 w-6 text-muted-foreground shrink-0" strokeWidth={1.5} />
        </div>
        <p className="text-[20px] font-medium tracking-tight text-foreground/90 mb-1">
          Nada por aqui
        </p>
        <p className="text-[12.5px] text-muted-foreground max-w-[280px]">
          Ajuste os filtros ou aguarde novas notificações.
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full" preventScrollChaining>
      <NotificationBulkActionsBar
        count={selectedIds.size}
        hasUnreadSelected={hasUnreadSelected}
        onMarkRead={onBulkMarkRead}
        onMarkUnread={onBulkMarkUnread}
        onArchive={onBulkArchive}
        onClear={onBulkClear}
      />

      <div className="pb-6">
        {grouped.map(({ bucket, items: bucketItems }) => (
          <Fragment key={bucket}>
            {selectedIds.size === 0 && (
              <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm px-4 pt-4 pb-2 flex items-center justify-between border-b border-border/40">
                <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {getDateBucketLabel(bucket)}
                </span>
                <span className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full bg-muted text-[10px] font-medium tabular-nums text-muted-foreground">
                  {bucketItems.length}
                </span>
              </div>
            )}
            {bucketItems.map(n => {
              const isActive = activeId === n.id;
              const isSelected = selectedIds.has(n.id);
              const unread = !n.is_read;
              const palette = getModulePalette(n.type);

              return (
                <div
                  key={n.id}
                  className={cn(
                    'group relative flex items-start gap-3 pl-4 pr-3 py-3 cursor-pointer transition-colors',
                    'border-b border-border/50',
                    !isActive && 'hover:bg-muted/50',
                    isActive && 'bg-primary/[0.06]',
                    unread && !isActive && 'bg-primary/[0.025]',
                  )}
                  onClick={() => onSelectItem(n.id)}
                >
                  {/* Rail vertical (ativo: cor do módulo) */}
                  {isActive && (
                    <span
                      aria-hidden
                      className={cn('absolute left-0 top-2 bottom-2 w-[2px] rounded-r', palette.rail)}
                    />
                  )}

                  {/* Checkbox (hover/selecionado) */}
                  <div
                    className={cn(
                      'pt-2 transition-opacity',
                      isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelected(n.id)}
                      aria-label="Selecionar notificação"
                      className="h-3.5 w-3.5 rounded-[3px]"
                    />
                  </div>

                  {/* Tile colorido por módulo */}
                  <div
                    className={cn(
                      'flex-shrink-0 h-8 w-8 rounded-md flex items-center justify-center',
                      palette.tile,
                    )}
                  >
                    <span className={palette.icon}>
                      {getNotificationIcon(n.type)}
                    </span>
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <p className={cn(
                        'text-[13px] leading-snug line-clamp-1 text-foreground',
                        unread ? 'font-semibold' : 'font-normal text-foreground/85',
                      )}>
                        {n.title}
                      </p>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-[11px] tabular-nums text-muted-foreground">
                          {formatEditorialDate(n.created_at)}
                        </span>
                        {unread && (
                          <span
                            aria-hidden
                            className={cn('h-1.5 w-1.5 rounded-full', palette.dot)}
                          />
                        )}
                      </div>
                    </div>
                    {n.message && (
                      <p className="text-[12px] text-muted-foreground line-clamp-1 mt-0.5">
                        {n.message}
                      </p>
                    )}
                    {n.archived_at && (
                      <span className="inline-flex items-center mt-1 px-1.5 h-[16px] rounded-sm bg-muted text-[9.5px] font-medium uppercase tracking-wider text-muted-foreground">
                        arquivada
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </Fragment>
        ))}
      </div>
    </ScrollArea>
  );
}
